import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PaymentService from "../../app/data/payment/paymentService";
import { PublicPaymentLinkView } from "../../app/data/payment/PaymentSession";
import Amount from "../../ui-components/molecules/amount/amount";
import Loading from "../../ui-components/loading/loading";
import XGSErrorMessage from "../../ui-components/error-message/errorMessage";
import { formatProbillDisplay } from "../shipments/labels-receipts/types";
import PublicPayPanel from "./publicPayPanel";
import PublicPayPortalPromo from "./publicPayPortalPromo";
import QuickPayHeader from "./quickPayHeader";
import "../shipments/labels-receipts/labelsReceipts.scss";
import "./publicPayment.scss";

const paymentService = PaymentService.getInstance();
const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const STATUS_COPY: Record<string, string> = {
  PAID: "This payment link has already been used.",
  EXPIRED: "This payment link has expired.",
  REVOKED: "This payment link is no longer active.",
};

const PublicPaymentLinkPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [link, setLink] = useState<PublicPaymentLinkView | null>(null);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    paymentService.getPublicPaymentLink(token).then((response) => {
      if (cancelled) {
        return;
      }
      setLoading(false);
      if (!response.ok() || !response.data) {
        const err = response.getError?.();
        setError(typeof err === "string" ? err : "This payment link could not be opened.");
        return;
      }
      setLink(response.data);
      setEmail(response.data.email || "");
    });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const payable = (link?.probills || []).filter((line) => line.openAmountCents > 0);

  return (
    <div className="xgs-public-payment">
      <QuickPayHeader />
      <div className="xgs-public-payment__content">
        <p className="xgs-public-payment__link-note">
          These XGS probill numbers are locked to this link. You can change the email before you pay.
        </p>
        <div className="xgs-public-payment__layout">
        <div className="xgs-public-payment__main">
        <Loading isLoading={loading} />
        {error && <XGSErrorMessage>{error}</XGSErrorMessage>}
        {link && link.status !== "ACTIVE" && (
          <section className="xgs-print-download__card">
            <p className="xgs-public-payment__status">
              {STATUS_COPY[link.status] || "This payment link is no longer active."}
            </p>
          </section>
        )}
        {link && link.status === "ACTIVE" && (
          <section className="xgs-print-download__card">
            <div className="xgs-print-download__card__header">
              <span className="xgs-print-download__step">1</span>
              <div>
                <div className="xgs-print-download__card__title">XGS probill numbers on this link</div>
                <div className="xgs-print-download__card__desc">
                  The amount charged is the current open balance.
                </div>
              </div>
            </div>
            <ul className="xgs-public-payment__probills">
              {(link.probills || []).map((line) => (
                <li key={line.probillNumber} className="xgs-public-payment__probill">
                  <span>{formatProbillDisplay(line.probillNumber)}</span>
                  <Amount amount={line.openAmountCents / 100} />
                </li>
              ))}
            </ul>
            <p className="xgs-public-payment__amount-label">Open amount</p>
            <div className="xgs-public-payment__amount">
              <Amount amount={(link.openAmountCents || 0) / 100} />
            </div>
            {link.openAmountCents > 0 ? (
              <PublicPayPanel
                email={email}
                onEmailChange={(value) => {
                  setEmail(value);
                  if (emailError) {
                    setEmailError(null);
                  }
                }}
                emailError={emailError}
                openAmountCents={link.openAmountCents}
                probills={payable}
                showProbills
                linkToken={token}
                onBeforePay={() => {
                  if (!EMAIL_PATTERN.test(email.trim())) {
                    setEmailError("Enter a valid email address.");
                    return false;
                  }
                  setEmailError(null);
                  return true;
                }}
              />
            ) : (
              <p className="xgs-public-payment__status">There is no open amount left to pay.</p>
            )}
          </section>
        )}
        </div>
        <PublicPayPortalPromo />
        </div>
      </div>
    </div>
  );
};

export default PublicPaymentLinkPage;
